import { Airline } from '@fcom/common/interfaces/airlines';
import { type Location } from '@fcom/core-api';
import { LocalDate } from '@fcom/core/utils';
import { type Attachment } from '@fcom/ui-components/components/interfaces';

export interface SpecialAssistanceSubmissionData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  cardNumber?: number;
  flightDate: LocalDate;
  flightNumber: string;
  origin: {
    locationCode: string;
  };
  destination: {
    locationCode: string;
  };
  reservationNumber: string;
  reasonForSpecialAttention?: string;
  ownWheelchair?: string;
  reasonForWheelchair?: string;
  ableToSitWithKneesBent?: string;
  needAssistanceWhere?: string;
  travelingWithAssistant?: string;
  medicalCondition?: string;
  medicalEquipment?: string;
  typeOfMedicalEquipment?: string;
  CAPAPDeviceModel?: string;
  portableOxygenConcentratorModel?: string;
  otherMedicalEquipmentType?: string;
  otherMedicalEquipmentModel?: string;
  addMedicalEquipment?: string;
  additionalInformation?: string;
  otherReasonForAssistance?: string;
  wheelchairType?: string;
  wheelchairDepth?: string;
  wheelchairHeight?: string;
  wheelchairWidth?: string;
  wheelchairWeight?: string;
  assistantName?: string;
  assistance?: string;
  lithiumBatteries?: string;
  lithiumBattery1Power?: string;
  lithiumBattery2Power?: string;
  otherMobilityAidDescription?: string;
}

export interface RequestRefundSubmissionData extends RequestRefundBankingData {
  refundType: string;
  refundReason: string;
  firstName: string;
  lastName: string;
  finnairPlusNumber?: string;
  phone: string;
  email: string;
  bookingReference: string;
  departureDate?: string;
  bookingDate?: string;
  cancellationDate?: string;
  additionalInformation?: string;
  refundItems: RefundItem[];
  useOnlyVoucher: boolean;
  flightNumber?: string;
}

export interface RequestRefundBankingData {
  iban?: string;
  bic?: string;
  bankAccountHolder?: string;
  bankAccountHolderAddress?: string;
  bankName?: string;
  bankAddress?: string;
  correspondentBankData?: string;
  bik?: string;
  inn?: string;
  patronymicName?: string;
  kpp?: string;
  ncc?: string;
  ifsc?: string;
}

export interface RefundItem {
  firstName: string;
  lastName: string;
  ticketNumber?: string;
  emdNumber?: string;
  refundItemType?: string;
  finnairPlusNumber?: string;
  preorderBagNumber?: string;
}

export interface SpecialAssistanceAPISubmissionData {
  contact: {
    Language: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber?: string;
    memberNumber?: number;
  };
  flight: {
    departureDate: string;
    flightNumber: string;
    source: string;
    destination: string;
    reservationNumber: string;
  };
  request: {
    reasonForRequest: string;
    otherReasonForAssistance?: string;
    additionalInformation?: string;
    ownWheelchair?: string;
    assistance?: string;
    wheelchairType?: string;
    wheelchairDepth?: string;
    wheelchairHeight?: string;
    wheelchairWidth?: string;
    wheelchairWeight?: string;
    addMedicalEquipment?: string;
    travelingWithAssistant?: string;
    assistantName?: string;
    medicalEquipment?: string;
    typeOfMedicalEquipment?: string;
    typeOfCondition?: string;
    ableToSitWithKneesBent?: string;
    reasonForWheelchair?: string;
    portableOxygenConcentratorModel?: string;
    CAPAPDeviceModel?: string;
    otherMedicalEquipmentModel?: string;
    otherMedicalEquipmentType?: string;
    lithiumBatteries?: string;
    lithiumBattery1Power?: string;
    lithiumBattery2Power?: string;
  };
}

export interface CaseAttachmentPayload {
  data: string;
  filename: string;
  origin: FileOrigin;
  salesforceCaseId?: string;
  caseId?: string;
  lastName?: string;
}

export enum FileOrigin {
  CASE = 'case',
  FEED = 'feed',
}

export interface CancellationCoverClaimFormData {
  bookingReference: string;
  ticketNumber: string;
  departureCity?: Location;
  destinationCity?: Location;
  departureDate?: LocalDate;
  returnDate?: LocalDate;
  firstName: string;
  familyName: string;
  email: string;
  country: string;
  phoneNumber?: string;
  refundFor: string;
  companions?: {
    firstName: string;
    familyName: string;
  }[];
  file: string;
  attachmentConsent: boolean;
  iban?: string;
  bic?: string;
  accountHolder?: string;
  bankName?: string;
}

export interface BaggageTracingAPISubmissionData {
  '01___Reference_number': string;
  '02___First_name': string;
  '03___Last_name': string;
  '04___Home_address': string;
  '05___Email': string;
  '06___Phone_number': string;
  '07___Mobile_number': string;
  '08___Temporary_address'?: string;
  '09___Temporary_address_valid_from'?: string;
  '10___Temporary_address_valid_to'?: string;
  '11___Plus_number'?: string;
  '12___Finnair_plus_tier': string;
  '13___Flight_1_from'?: string;
  '13___Flight_1_to'?: string;
  '13___Flight_1'?: string;
  '13___Flight_1_date'?: string;
  '13___Flight_2_from'?: string;
  '13___Flight_2_to'?: string;
  '13___Flight_2'?: string;
  '13___Flight_2_date'?: string;
  '13___Flight_3_from'?: string;
  '13___Flight_3_to'?: string;
  '13___Flight_3'?: string;
  '13___Flight_3_date'?: string;
  '13___Flight_4_from'?: string;
  '13___Flight_4_to'?: string;
  '13___Flight_4'?: string;
  '13___Flight_4_date'?: string;
  '14___Baggage_last_seen': string;
  '15___Number_of_pieces_checked': number;
  '16___Baggage_Identification_Tag_numbers': string;
  '17___When_and_where_loss_reported': string;
  '18___Baggage_insurance_policy_number': string;
  '19___Insurance_company_name_and_address': string;
  '20___Anything_particular_at_check_in': string;
  '21___Colour': string;
  '22___Brand'?: string;
  '23___Type_of_bag'?: string;
  '24___Bag_was'?: string;
  '25___Bag_had_zipper': string;
  '26___Bag_had_lock': string;
  '27___Bag_had_combination_lock': string;
  '28___Lock_number_combination': string;
  '30___Bag_had_wheels': string;
  '32___Bag_had_retractable_handle': string;
  '33___Baggage_name_tag_description': string;
  '34___Baggage_identifiable_features': string;
  '35___Item_1_quantity'?: string;
  '35___Item_1'?: string;
  '35___Item_1_description'?: string;
  '35___Item_1_belongs_to'?: BaggageTracingItemOwnership;
  '37___Item_2_quantity'?: string;
  '37___Item_2'?: string;
  '37___Item_2_description'?: string;
  '37___Item_2_belongs_to'?: BaggageTracingItemOwnership;
  '38___Item_3_quantity'?: string;
  '38___Item_3'?: string;
  '38___Item_3_description'?: string;
  '38___Item_3_belongs_to'?: BaggageTracingItemOwnership;
  '39___Item_4_quantity'?: string;
  '39___Item_4'?: string;
  '39___Item_4_description'?: string;
  '39___Item_4_belongs_to'?: BaggageTracingItemOwnership;
  '40___Item_5_quantity'?: string;
  '40___Item_5'?: string;
  '40___Item_5_description'?: string;
  '40___Item_5_belongs_to'?: BaggageTracingItemOwnership;
  '41___Item_6_quantity'?: string;
  '41___Item_6'?: string;
  '41___Item_6_description'?: string;
  '41___Item_6_belongs_to'?: BaggageTracingItemOwnership;
  '50___Items_to_identify_baggage'?: string;
  __addressKey: string;
  __messageSubject: string;
  __confirmationTo: string;
  __emailType: string;
}
/* tslint:enable */

export type BaggageTracingItemOwnership = 'MALE' | 'FEMALE' | 'CHILD';

export interface BaggageTracingSubmissionData {
  baggageReferenceNumber: string;
  firstName: string;
  lastName: string;
  phonePrefix: string;
  phoneNumber: string;
  email: string;
  homeAddress: string;
  temporaryAddress?: string;
  temporaryAddressValidFrom?: LocalDate;
  temporaryAddressValidTo?: LocalDate;
  cardNumber?: string;
  finnairPlusTier?: string;
  itenerary: BaggageTracingItenerary[];
  baggageLastSeen: string;
  numberOfPiecesChecked: number;
  baggageIdentificationTagNumbers: string;
  whenAndWhereLossReported: string;
  baggageInsurancePolicyNumber: string;
  insuranceCompanyNameAndAddress: string;
  anythingParticularAtCheckIn: string;
  color: string;
  brand?: string;
  typeOfBag?: string;
  bagWasHard?: string;
  bagOptions?: boolean[];
  combinationLockCode?: string;
  baggageNameTagDescription: string;
  baggageIdentifiableFeatures: string;
  bagItems: BaggageTracingBag[];
  itemsToIdentifyBaggage: string;
}

export interface BaggageTracingItenerary {
  destination: string;
  origin: string;
  flightDate: LocalDate;
  flightNumber: string;
}

export interface BaggageTracingBag {
  itemQuantity: number;
  itemName: string;
  itemDescription: string;
  itemBelongsTo: BaggageTracingItemOwnership;
}

export interface LoggedInMissingPointsSubmissionData {
  airline: string;
  destinationAirport?: Location;
  bookingClassSold?: string;
  originAirport?: Location;
  flightDate?: LocalDate;
  flightNumber?: string;
  sequenceNumber?: string;
  ticketNumber: string;
}

export interface LoggedOutMissingPointsSubmissionData {
  passengerName: string;
  memberNumber: string;
  airline: Airline;
  ticketNumber?: string;
  flightFrom: Location;
  flightTo: Location;
  departureDate: LocalDate;
  returnDate?: LocalDate;
  flightNumbers: { flightNumber: string }[];
  companions?: {
    companionName: string;
    companionMemberNumber: string;
  }[];
  message?: string;
  email: string;
  phone: string;
}

export enum ClaimMissingPointsFormOrigin {
  FROM_FLIGHTS = 'fromFlights',
  FROM_PARTNERS = 'fromPartners',
}

export interface ClaimPartnerMissingAviosSubmissionData {
  claimMissingPointsForm: ClaimMissingPointsFormOrigin;
  passengerName?: string;
  memberNumber?: string;
  message?: string;
  email?: string;
  attachment?: string;
  attachmentName?: string;
  locale?: string;
}

export interface ClaimFlightsMissingAviosFormData {
  passengerName?: string;
  memberNumber?: string;
  airline?: Airline;
  ticketNumber?: string;
  bookingClass?: string;
  flightFrom?: Location;
  flightTo?: Location;
  departureDate?: LocalDate;
  returnDate?: LocalDate;
  flightNumbers?: { flightNumber: string }[];
  companions?: {
    companionName: string;
    companionMemberNumber: string;
  }[];
  message?: string;
  email?: string;
  phone?: string;
  attachment?: Attachment | Attachment[];
}

export interface ClaimFlightsMissingAviosSubmissionData {
  claimMissingPointsForm: ClaimMissingPointsFormOrigin;
  airline?: Airline;
  ticketNumber?: string;
  memberNumber?: string;
  passengerName?: string;
  bookingClass?: string;
  flightDetails?: ClaimMissingAviosFlightDetailSubmissionData;
  message?: string;
  email?: string;
  phone?: string;
  companions?: {
    passengerName: string;
    memberNumber: string;
  }[];
  attachment?: string;
  attachmentName?: string;
  locale?: string;
}
export interface ClaimMissingAviosFlightDetailSubmissionData {
  ticketNumber?: string;
  bookingClass?: string;
  flightFrom: string;
  flightTo: string;
  departureDate: string;
  returnDate?: string;
  flightNumbers: string[];
}

export enum CancellationCoverClaimFor {
  DEPARTURE_AND_RETURN_FLIGHTS = 'departure and return flights',
  RETURN_FLIGHTS = 'return flights',
}

export interface CancellationCoverClaimSubmissionData {
  bookingReference: string;
  ticketNumber: string;
  departureCity?: string;
  destinationCity?: string;
  departureDate?: string;
  returnDate?: string;
  firstName: string;
  familyName: string;
  email: string;
  country: string;
  phone?: string;
  refundFor: CancellationCoverClaimFor;
  companions?: {
    firstName: string;
    familyName: string;
  }[];
  attachment: string;
  attachmentConsent: string;
  attachmentName: string;
  iban?: string;
  bic?: string;
  accountHolder?: string;
  bankName?: string;
  locale?: string;
}
